import api from './api.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface PoMilestoneDTO {
  id: string;
  purchase_order_id: string;
  milestone_number: number;
  milestone_name: string;
  description?: string | null;
  milestone_type: string;
  fulfillment_method: string;
  percentage: number;
  milestone_amount: number;
  expected_date?: string | null;
  trigger_condition?: string | null;
  supporting_deliverable?: string | null;
  invoice_requirement?: string | null;
  tax_applicable?: boolean;
  retention_applicable?: boolean;
  retention_percentage?: number;
  status: string;
  completed_value?: number;
  received_value?: number;
  accepted_value?: number;
  rejected_value?: number;
  invoiced_value?: number;
  paid_value?: number;
  balance_value?: number;
  remarks?: string | null;
}

export interface PoMilestoneInput {
  id?: string;
  milestone_number?: number;
  milestone_name: string;
  description?: string;
  milestone_type?: string;
  fulfillment_method?: string;
  percentage?: number;
  milestone_amount?: number;
  expected_date?: string;
  trigger_condition?: string;
  supporting_deliverable?: string;
  invoice_requirement?: string;
  tax_applicable?: boolean;
  retention_applicable?: boolean;
  retention_percentage?: number;
  remarks?: string;
}

export interface PoMilestoneRegisterRow extends PoMilestoneDTO {
  po_number?: string;
  supplier_name?: string;
  po_grand_total?: number;
}

export interface PoMilestoneDashboardSummary {
  dueCount: number;
  pendingAcceptance: number;
  pendingReceipts: number;
  pendingInvoices: number;
}

export const poMilestoneService = {
  listForPo(poId: string): Promise<PoMilestoneDTO[]> {
    return api
      .get(`/procurement/purchase-orders/${poId}/milestones`)
      .then((res) => unwrap<PoMilestoneDTO[]>(res) ?? []);
  },

  applyAction(
    milestoneId: string,
    action: 'complete' | 'accept' | 'reject' | 'returnForCorrection',
    payload?: Record<string, unknown>
  ): Promise<PoMilestoneDTO> {
    return api
      .post(`/procurement/po-milestones/${milestoneId}/action`, { action, ...payload })
      .then((res) => unwrap<PoMilestoneDTO>(res));
  },

  register(params?: { supplier_id?: string; status?: string }): Promise<PoMilestoneRegisterRow[]> {
    const q = new URLSearchParams();
    if (params?.supplier_id) q.set('supplier_id', params.supplier_id);
    if (params?.status) q.set('status', params.status);
    return api
      .get(`/procurement/po-milestones/register?${q.toString()}`)
      .then((res) => unwrap<PoMilestoneRegisterRow[]>(res) ?? []);
  },

  due(): Promise<PoMilestoneRegisterRow[]> {
    return api.get('/procurement/po-milestones/due').then((res) => unwrap<PoMilestoneRegisterRow[]>(res) ?? []);
  },

  dashboardSummary(): Promise<PoMilestoneDashboardSummary> {
    return api
      .get('/procurement/po-milestones/dashboard-summary')
      .then((res) => unwrap<PoMilestoneDashboardSummary>(res));
  },

  pendingAcceptance(): Promise<PoMilestoneDTO[]> {
    return api
      .get('/procurement/po-milestones/pending-acceptance')
      .then((res) => unwrap<PoMilestoneDTO[]>(res) ?? []);
  },

  paymentForecast(days = 90) {
    return api
      .get(`/procurement/po-milestones/payment-forecast?days=${days}`)
      .then((res) => unwrap<any[]>(res) ?? []);
  },

  supplierExposure() {
    return api
      .get('/procurement/po-milestones/supplier-exposure')
      .then((res) => unwrap<any[]>(res) ?? []);
  },

  createAdvanceFromMilestone(
    poId: string,
    milestoneId: string,
    body?: { requested_amount?: number; reason?: string; remarks?: string }
  ) {
    return api
      .post(`/procurement/purchase-orders/${poId}/milestones/${milestoneId}/advance-request`, body ?? {})
      .then((res) => unwrap<any>(res));
  },
};
